import { Button, Grid } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  BasicButtonProps,
  CancelButtonProps,
  MarginSize,
  RedirectButtonProps,
  SecondaryButtonProps
} from '.'
import { FormattedMessage } from 'react-intl'
import { useRedirect } from '../hooks'
import { MaybeStringOrNumber } from '../utils'

const SecondaryButton = ({
  children,
  translationId,
  ...rest
}: SecondaryButtonProps) => {
  const translatedTitle = translationId ? (
    <FormattedMessage id={translationId} />
  ) : undefined

  return (
    <Button color="secondary" {...rest}>
      {translatedTitle}
      {children}
    </Button>
  )
}

export const BasicButton = ({ children, ...rest }: BasicButtonProps) => {
  return (
    <SecondaryButton {...rest} disableElevation size="large" variant="outlined">
      {children}
    </SecondaryButton>
  )
}

export const CancelButton = ({ ...props }: CancelButtonProps) => {
  return <BasicButton translationId="common.cancel" type="button" {...props} />
}

type ButtonDisplayType = 'block'

interface GridButtonProps
  extends Pick<
    SecondaryButtonProps,
    'onClick' | 'startIcon' | 'translationId'
  > {
  buttonDisplayType?: ButtonDisplayType
  marginSize?: MarginSize
}

export const GridButton = ({
  buttonDisplayType,
  marginSize = MarginSize.NARROW,
  ...buttonProps
}: GridButtonProps) => {
  return (
    <Grid
      item
      marginTop={marginSize === MarginSize.NARROW ? '1rem' : undefined}
      md={buttonDisplayType === 'block' ? 12 : 4}
      xs={12}
    >
      <SecondaryButton {...buttonProps} />
    </Grid>
  )
}

export type AddButtonProps = Omit<
  GridButtonProps,
  'buttonDisplay' | 'startIcon'
>

export const AddButton = ({
  marginSize = MarginSize.NARROW,
  ...rest
}: AddButtonProps) => {
  return (
    <GridButton {...rest} marginSize={marginSize} startIcon={<AddIcon />} />
  )
}

type DeleteButtonProps = Omit<GridButtonProps, 'startIcon'>

export const DeleteButton = ({
  marginSize = MarginSize.NONE,
  ...props
}: DeleteButtonProps) => {
  return <GridButton marginSize={marginSize} {...props} />
}

export const RedirectButton = ({
  redirectPath,
  translationId
}: RedirectButtonProps) => {
  const { redirect } = useRedirect()

  return (
    <BasicButton
      onClick={() => redirect(redirectPath)}
      translationId={translationId}
      type="button"
    />
  )
}

interface CopyButtonProps
  extends Omit<SecondaryButtonProps, 'onClick' | 'startIcon'> {
  content?: MaybeStringOrNumber
}

export const CopyButton = ({ content, ...rest }: CopyButtonProps) => {
  const handleClick = () => {
    if (content) {
      navigator.clipboard.writeText(content.toString())
    }
  }
  return (
    <SecondaryButton
      {...rest}
      onClick={handleClick}
      startIcon={<ContentCopyIcon style={{ fontSize: 'small' }} />}
      translationId={rest.translationId}
    />
  )
}
